import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useHistory } from "react-router-dom";
import axios from "axios";
import { FaSearch, FaTimes } from "react-icons/fa";
import { getProductImageUrl, handleProductImageError } from "../../util/storeLogoHelper";
import { clearRecentlyViewed, getRecentlyViewed } from "../../util/recentViewedService";

function withAlpha(hex, alpha) {
  const raw = String(hex || "").replace("#", "");
  if (raw.length !== 6) return `rgba(0, 0, 0, ${alpha})`;
  const value = parseInt(raw, 16);
  const red = (value >> 16) & 255;
  const green = (value >> 8) & 255;
  const blue = value & 255;
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function searchLook(settings) {
  const theme = settings || {};
  return {
    background: theme.searchBackground || theme.headerBackground || "#111111",
    color: theme.searchTextColor || theme.headerTextColor || "#ffffff",
    inputBackground: theme.searchInputBackground || "#ffffff",
    inputText: theme.searchInputText || "#111827",
    cardBackground: theme.searchCardBackground || "#ffffff",
    nameColor: theme.searchNameColor || "#111827",
    priceColor: theme.searchPriceColor || "#6b7280",
    overlay: withAlpha(theme.searchOverlay || "#000000", 0.45),
    placeholder: theme.searchPlaceholder || "Search",
    recentLabel: theme.searchRecentLabel || "Recently viewed",
    productsLabel: theme.searchProductsLabel || "Products",
    clearLabel: theme.searchClearLabel || "Clear",
  };
}

function money(value) {
  const amount = Number(value);
  if (!amount) return "";
  return `Rs. ${amount.toLocaleString()}`;
}

function productHref(item) {
  const category = item.categorySlug || item.category?.slug;
  const slug = item.productSlug || item.slug;
  if (!category || !slug) return "/search";
  return `/collection/${category}/${slug}`;
}

function productName(item) {
  return item.label || item.name || "Product";
}

function SearchCard({ item, look, onPick }) {
  const price = Number(item.price || item.sellingPrice || 0);
  const original = Number(item.original_price || item.originalPrice || 0);
  const subtitle = item.brand || item.categoryName || "";
  return (
    <Link to={productHref(item)} className="search-modal-card" style={{ background: look.cardBackground, color: look.nameColor }} onClick={onPick}>
      <span className="search-modal-photo">
        <img src={getProductImageUrl(item)} alt={productName(item)} onError={handleProductImageError} />
      </span>
      <span className="search-modal-name">{productName(item)}</span>
      {subtitle && subtitle !== productName(item) && (
        <span className="search-modal-meta" style={{ color: look.priceColor }}>{subtitle}</span>
      )}
      {price > 0 && (
        <span className="search-modal-price" style={{ color: look.priceColor }}>
          {original > price && <s>{money(original)}</s>}
          <span>{money(price)}</span>
        </span>
      )}
    </Link>
  );
}

export default function SearchModal({ open, onClose, settings }) {
  const look = searchLook(settings);
  const history = useHistory();
  const inputRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [resultQuery, setResultQuery] = useState("");
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);

  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    setQuery("");
    setProducts([]);
    setResultQuery("");
    setRecent(getRecentlyViewed());
    const refreshRecent = () => setRecent(getRecentlyViewed());
    const onKey = (event) => {
      if (event.key === "Escape") onCloseRef.current();
    };
    window.addEventListener("recentlyViewedUpdated", refreshRecent);
    window.addEventListener("keydown", onKey);
    const focusTimer = setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 40);
    return () => {
      window.removeEventListener("recentlyViewedUpdated", refreshRecent);
      window.removeEventListener("keydown", onKey);
      clearTimeout(focusTimer);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const trimmed = query.trim();
    let cancelled = false;
    const timer = setTimeout(() => {
      setLoading(true);
      const url = trimmed
        ? `/api/view-products-search?q=${encodeURIComponent(trimmed)}&limit=8`
        : "/api/view-products-search?all=1&limit=8";
      axios
        .get(url)
        .then((res) => {
          if (cancelled || res.data.status !== 200) return;
          setProducts(res.data.data || []);
          setResultQuery(trimmed);
        })
        .catch(() => {
          if (cancelled) return;
          setProducts([]);
          setResultQuery(trimmed);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, trimmed ? 280 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, query]);

  if (!open) return null;

  const submit = (event) => {
    event.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;
    onClose();
    history.push(`/search?q=${encodeURIComponent(trimmed)}`);
  };

  return createPortal(
    <div className="search-modal" role="dialog" aria-modal="true" aria-label="Search">
      <button type="button" className="search-modal-backdrop" style={{ background: look.overlay }} aria-label="Close search" onClick={onClose} />
      <div className="search-modal-panel" style={{ background: look.background, color: look.color }}>
        <form className="search-modal-bar" onSubmit={submit}>
          <label className="search-modal-field" style={{ background: look.inputBackground, color: look.inputText }}>
            <FaSearch size={14} />
            <input
              ref={inputRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={look.placeholder}
              aria-label={look.placeholder}
              style={{ color: look.inputText }}
            />
          </label>
          <button type="button" className="search-modal-close" onClick={onClose} aria-label="Close search" style={{ color: look.color }}>
            <FaTimes size={16} />
          </button>
        </form>

        <div className="search-modal-body">
          {query.trim() === "" && recent.length > 0 && (
            <section>
              <div className="search-modal-heading">
                <span>{look.recentLabel}</span>
                <button type="button" onClick={clearRecentlyViewed}>{look.clearLabel}</button>
              </div>
              <div className="search-modal-grid">
                {recent.slice(0, 4).map((item) => (
                  <SearchCard key={`recent-${item.id}`} item={item} look={look} onPick={onClose} />
                ))}
              </div>
            </section>
          )}

          {query.trim() === "" || resultQuery === query.trim() ? (
            <section>
              <div className="search-modal-heading">
                <span>{look.productsLabel}</span>
              </div>
              {products.length > 0 ? (
                <div className="search-modal-grid">
                  {products.slice(0, 8).map((item) => (
                    <SearchCard key={item.id || item.productSlug} item={item} look={look} onPick={onClose} />
                  ))}
                </div>
              ) : (
                <p className="search-modal-empty">{loading ? "Searching..." : "No products found"}</p>
              )}
            </section>
          ) : (
            <p className="search-modal-empty">Searching...</p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
